import {
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import {
  useGetTeamMembersQuery,
  useRemoveTeamMemberMutation,
  useUpdateTeamMembersDisplayMutation,
} from '../../../../../api/teams'
import type { IUpdateTeamMembersDisplayMemberInput } from '../../../../../api/types/teams'
import type { IProjectTeamMemberResource } from '../../../../../api/types/projects'
import { extractApiErrorMessage } from '../../../../../utils/api'
import {
  SortOrderField,
  clampSortOrder,
  normalizeSortOrderFromApi,
} from '../../../EditProject/Tabs/EditTeamForm/SortOrderField'
import {
  editProjectFieldSx,
  editProjectSelectFieldSx,
} from '../../../EditProject/Tabs/editProjectFieldSx'

const ROLE_LABEL_MAX_LEN = 40
const MAX_FEATURED_NON_COFOUNDERS = 4

function countFeaturedNonCofounders(members: IUpdateTeamMembersDisplayMemberInput[]): number {
  return members.filter((item) => item.team_role !== 'coauthor' && item.is_featured).length
}

export const EditTeamMembersForm = () => {
  const { id } = useParams<{ id: string }>()
  const teamId = Number(id)
  const { data: members = [], isLoading } = useGetTeamMembersQuery(
    { teamId },
    { skip: !id || Number.isNaN(teamId) }
  )
  const [updateTeamMembersDisplay, updateState] = useUpdateTeamMembersDisplayMutation()
  const [removeTeamMember, removeState] = useRemoveTeamMemberMutation()

  const [removeTarget, setRemoveTarget] = useState<{ userId: number; title: string } | null>(null)

  const founderMember = useMemo(
    () => members.find((member: IProjectTeamMemberResource) => member.is_author) ?? null,
    [members]
  )
  const editableMembers = useMemo(
    () => members.filter((member: IProjectTeamMemberResource) => !member.is_author),
    [members]
  )

  const [draft, setDraft] = useState<IUpdateTeamMembersDisplayMemberInput[]>([])
  const [founderRoleLabel, setFounderRoleLabel] = useState<string | null>(null)
  const [founderIsFeatured, setFounderIsFeatured] = useState<boolean | null>(null)
  const [founderSortOrder, setFounderSortOrder] = useState<number | null>(null)

  const values: IUpdateTeamMembersDisplayMemberInput[] =
    draft.length > 0
      ? draft
      : editableMembers.map((member: IProjectTeamMemberResource) => ({
          user_id: member.user_id,
          team_role: member.team_role === 'coauthor' ? 'coauthor' : 'member',
          role_label: member.role_label,
          is_featured: member.is_featured,
          sort_order: normalizeSortOrderFromApi(member.sort_order),
        }))

  const patchRow = (userId: number, patch: Partial<IUpdateTeamMembersDisplayMemberInput>) => {
    setDraft((prev) => {
      const base = prev.length > 0 ? prev : values
      return base.map((item) => {
        if (item.user_id !== userId) {
          return item
        }

        return {
          ...item,
          ...patch,
          team_role: (patch.team_role ?? item.team_role) as 'member' | 'coauthor',
        }
      })
    })
  }

  const featuredNonCofoundersCount = useMemo(() => countFeaturedNonCofounders(values), [values])

  const handleSave = async () => {
    if (featuredNonCofoundersCount > MAX_FEATURED_NON_COFOUNDERS) {
      toast.error(
        `В превью можно показать не более ${MAX_FEATURED_NON_COFOUNDERS} участников без роли «Сооснователь».`
      )
      return
    }

    const resolvedFounderRoleLabel =
      (founderRoleLabel ?? founderMember?.role_label ?? '')?.trim() || null
    const resolvedFounderIsFeatured = founderIsFeatured ?? founderMember?.is_featured ?? true
    const resolvedFounderSortOrder = clampSortOrder(
      founderSortOrder ?? normalizeSortOrderFromApi(founderMember?.sort_order)
    )

    try {
      await updateTeamMembersDisplay({
        teamId,
        body: {
          founder_display: {
            role_label: resolvedFounderRoleLabel,
            is_featured: resolvedFounderIsFeatured,
            sort_order: resolvedFounderSortOrder,
          },
          members: values.map((item) => ({
            ...item,
            role_label: item.team_role === 'coauthor' ? null : (item.role_label?.trim() || null),
          })),
        },
      }).unwrap()
      toast.success('Участники обновлены')
      setDraft([])
      setFounderRoleLabel(resolvedFounderRoleLabel)
      setFounderIsFeatured(resolvedFounderIsFeatured)
      setFounderSortOrder(resolvedFounderSortOrder)
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить участников')
    }
  }

  const handleConfirmRemove = async () => {
    if (!removeTarget) {
      return
    }

    try {
      await removeTeamMember({ teamId, userId: removeTarget.userId }).unwrap()
      setDraft((prev) => prev.filter((item) => item.user_id !== removeTarget.userId))
      setRemoveTarget(null)
      toast.success('Участник исключён из команды')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось исключить участника')
    }
  }

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
      <Stack gap={2}>
        <Typography variant="black-16-semiBold">Участники команды</Typography>
        <Typography variant="black-14-light" color="text.secondary">
          В превью — до {MAX_FEATURED_NON_COFOUNDERS} участников без роли «Сооснователь». Основатель и
          сооснователи настраиваются отдельно.
        </Typography>

        {isLoading && <Typography>Загрузка участников…</Typography>}
        {!isLoading && !founderMember && values.length === 0 && (
          <Typography variant="black-14-light">Активных участников пока нет.</Typography>
        )}

        {!isLoading && founderMember && (
          <Stack gap={1} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            <Typography variant="black-14-medium">Вы (основатель)</Typography>
            <Stack gap={0.5}>
              <Typography variant="black-14-medium">
                {(founderMember.user.fullname ?? founderMember.user.shortname ?? '').trim() ||
                  'Пользователь'}
              </Typography>
            </Stack>
            <TextField
              size="small"
              placeholder="Ваша роль в команде"
              value={founderRoleLabel ?? founderMember.role_label ?? ''}
              onChange={(event) => setFounderRoleLabel(event.target.value.slice(0, ROLE_LABEL_MAX_LEN))}
              fullWidth
              sx={editProjectFieldSx}
              inputProps={{
                'aria-label': 'Ваша роль в команде',
              }}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
              <SortOrderField
                value={clampSortOrder(
                  founderSortOrder ?? normalizeSortOrderFromApi(founderMember.sort_order)
                )}
                onChange={setFounderSortOrder}
                sx={{ ...editProjectFieldSx, width: { xs: '100%', md: 160 } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={founderIsFeatured ?? founderMember.is_featured}
                    onChange={(event) => setFounderIsFeatured(event.target.checked)}
                  />
                }
                label="Показывать в превью"
              />
            </Stack>
          </Stack>
        )}

        {!isLoading &&
          values.map((item) => {
            const member = editableMembers.find(
              (x: IProjectTeamMemberResource) => x.user_id === item.user_id
            )
            const title =
              (member?.user.fullname ?? member?.user.shortname ?? '').trim() || `ID ${item.user_id}`
            const isCofounder = item.team_role === 'coauthor'
            const canEnableFeatured =
              item.is_featured || featuredNonCofoundersCount < MAX_FEATURED_NON_COFOUNDERS
            return (
              <Stack
                key={item.user_id}
                gap={1}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
              >
                <Typography variant="black-14-medium">{title}</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                  <TextField
                    select
                    size="small"
                    value={item.team_role}
                    onChange={(event) =>
                      patchRow(item.user_id, {
                        team_role: event.target.value as 'member' | 'coauthor',
                        role_label: event.target.value === 'coauthor' ? null : item.role_label,
                      })
                    }
                    fullWidth
                    sx={editProjectSelectFieldSx}
                    SelectProps={{
                      MenuProps: {
                        disableScrollLock: true,
                      },
                    }}
                    inputProps={{
                      'aria-label': 'Роль в команде',
                    }}
                  >
                    <MenuItem value="member">Участник</MenuItem>
                    <MenuItem value="coauthor">Сооснователь</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    placeholder="Подпись роли"
                    value={item.role_label ?? ''}
                    onChange={(event) =>
                      patchRow(item.user_id, {
                        role_label: event.target.value.slice(0, ROLE_LABEL_MAX_LEN),
                      })
                    }
                    disabled={isCofounder}
                    fullWidth
                    sx={editProjectFieldSx}
                  />
                  <SortOrderField
                    value={normalizeSortOrderFromApi(item.sort_order)}
                    onChange={(sort_order: number) => patchRow(item.user_id, { sort_order })}
                    sx={{ ...editProjectFieldSx, width: { xs: '100%', md: 160 } }}
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={item.is_featured}
                      onChange={(event) => patchRow(item.user_id, { is_featured: event.target.checked })}
                      disabled={isCofounder || (!item.is_featured && !canEnableFeatured)}
                    />
                  }
                  label="Показывать в превью"
                />
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => setRemoveTarget({ userId: item.user_id, title })}
                    disabled={removeState.isLoading}
                  >
                    Убрать из команды
                  </Button>
                </Stack>
              </Stack>
            )
          })}

        <Stack direction="row" spacing={1}>
          <Button variant="follow" onClick={handleSave} disabled={updateState.isLoading}>
            {updateState.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Сохранение...</span>
              </Stack>
            ) : (
              'Сохранить'
            )}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={removeTarget !== null} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Убрать из команды</DialogTitle>
        <DialogContent>
          <Typography variant="black-14-normal">
            {removeTarget
              ? `Участник «${removeTarget.title}» будет исключён из команды. Продолжить?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="follow" onClick={() => setRemoveTarget(null)} disabled={removeState.isLoading}>
            Отмена
          </Button>
          <Button variant="danger" onClick={() => void handleConfirmRemove()} disabled={removeState.isLoading}>
            {removeState.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Исключаем...</span>
              </Stack>
            ) : (
              'Убрать'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
